import { useMemo, useState } from 'react';
import { useSearchParams } from 'react-router-dom';
import { useQuery } from '@tanstack/react-query';
import { CalendarClock, CheckSquare, ListChecks, Plus } from 'lucide-react';
import {
  TASK_DUE_BUCKETS,
  isChecklistItemDone,
  type Checklist,
  type ChecklistItem,
  type TaskDueBucket,
  type TaskListItem,
  type TaskStatusFilter,
  type UpdateChecklistItemInput,
  type User,
} from '@peles/shared';
import {
  useBulkUpdateTasks,
  useChecklists,
  useCreateChecklist,
  useDeleteChecklistItem,
  useTasks,
  useToggleChecklistItem,
  useUpdateChecklistItem,
} from '../../hooks/useEventOps';
import { useLocalDay } from '../../hooks/useDashboard';
import { useEventUsers } from '../../hooks/useEvents';
import { useAuth } from '../../context/AuthContext';
import { useDialog } from '../../context/DialogContext';
import * as usersApi from '../../api/users';
import { SegmentedControl } from '../common/SegmentedControl';
import { TaskDueView } from './TaskDueView';
import { TaskListsView } from './TaskListsView';
import { TaskDetailModal } from './TaskDetailModal';
import { TaskFilterBar, type TaskFilters } from './TaskFilterBar';
import type { BulkChange } from './TaskBulkBar';
import { taskAssignees } from './taskUi';

type TaskView = 'due' | 'lists';

const VIEW_OPTIONS: { id: TaskView; label: string; icon: JSX.Element }[] = [
  { id: 'due', label: 'By due date', icon: <CalendarClock size={14} /> },
  { id: 'lists', label: 'By list', icon: <ListChecks size={14} /> },
];

const STATUSES = new Set<TaskStatusFilter>(['open', 'done', 'all']);

function csvParam(params: URLSearchParams, key: string): string[] {
  return (params.get(key) ?? '').split(',').filter(Boolean);
}

/** An open task, or its context for the editor. */
interface Editing {
  task: ChecklistItem;
  isTemplate: boolean;
  eventStartAt: Date | string | null;
  context: string;
}

export interface TasksScreenProps {
  /** One event's tasks; every event's when absent. */
  eventId?: string;
  /** The checklist templates instead — by list only, with offsets for dates. */
  templates?: boolean;
  /** Rendered before the title — the event page's "Back" link. */
  leading?: React.ReactNode;
}

/**
 * The one tasks screen, for an event's Tasks tab and for every event at
 * /tasks: tasks by due date or by list, with the same filters, the same row,
 * the same editor and the same bulk actions. Every filter lives in the
 * address, so a filtered view can be bookmarked or shared.
 */
export function TasksScreen({ eventId, templates = false, leading }: TasksScreenProps) {
  const [params, setParams] = useSearchParams();
  const { date: todayKey } = useLocalDay();
  const { user } = useAuth();
  const { confirm } = useDialog();

  const view: TaskView = templates
    ? 'lists'
    : params.get('view') === 'due' || params.get('view') === 'lists'
      ? (params.get('view') as TaskView)
      : eventId
        ? 'lists'
        : 'due';
  const statusParam = params.get('status') as TaskStatusFilter | null;
  const filters: TaskFilters = {
    q: params.get('q') ?? '',
    assignee: csvParam(params, 'assignee'),
    due: csvParam(params, 'due').filter((d): d is TaskDueBucket =>
      (TASK_DUE_BUCKETS as readonly string[]).includes(d),
    ),
    status: statusParam && STATUSES.has(statusParam) ? statusParam : 'open',
  };

  function update(next: Partial<Record<'view' | 'q' | 'assignee' | 'due' | 'status', string>>) {
    setParams(
      (prev) => {
        const out = new URLSearchParams(prev);
        for (const [key, value] of Object.entries(next)) {
          if (value) out.set(key, value);
          else out.delete(key);
        }
        return out;
      },
      { replace: true },
    );
  }

  function setFilters(next: TaskFilters) {
    update({
      q: next.q,
      assignee: next.assignee.join(','),
      due: next.due.join(','),
      status: next.status === 'open' ? '' : next.status,
    });
  }

  const { data: users = [], isLoading: usersLoading } = useQuery<User[]>({
    queryKey: ['users'],
    queryFn: () => usersApi.listUsers({ isActive: true }),
  });
  const { data: checklists = [], isLoading: listsLoading } = useChecklists(eventId, { templates });
  const { data: eventClients = [] } = useEventUsers(eventId ?? '');
  const { data: summary } = useTasks({ eventId, date: todayKey, limit: 0 }, !templates);

  const toggle = useToggleChecklistItem();
  const updateItem = useUpdateChecklistItem();
  const deleteItem = useDeleteChecklistItem();
  const createChecklist = useCreateChecklist();
  const bulk = useBulkUpdateTasks();

  const [editing, setEditing] = useState<Editing | null>(null);
  const assignees = useMemo(
    () =>
      // Anyone a task is already assigned to stays findable and assignable,
      // whoever they are — not only while their task is open in the editor.
      taskAssignees(
        users,
        [
          editing?.task.assignedToUserId,
          ...checklists.flatMap((c) =>
            c.sections.flatMap((s) => s.items.map((i) => i.assignedToUserId)),
          ),
        ],
        // On an event, its clients who manage their own tasks in the portal.
        eventClients
          .filter((c) => c.permissions.canManageTasks)
          .map((c) => ({ id: c.userId, name: c.userName })),
      ),
    [users, editing, checklists, eventClients],
  );

  const eventStartAt = eventId ? (checklists.find((c) => c.event)?.event?.startAt ?? null) : null;

  const onToggle = (task: ChecklistItem) =>
    toggle.mutate({ itemId: task.id, isDone: !isChecklistItemDone(task) });

  async function onDelete(task: ChecklistItem) {
    if (
      await confirm({
        title: 'Delete task',
        message: `Delete “${task.title}”?`,
        confirmLabel: 'Delete',
        variant: 'danger',
      })
    ) {
      deleteItem.mutate(task.id);
      if (editing?.task.id === task.id) setEditing(null);
    }
  }

  async function onBulk(itemIds: string[], change: BulkChange): Promise<boolean> {
    if (
      change.action === 'delete' &&
      !(await confirm({
        title: 'Delete tasks',
        message: `Delete ${itemIds.length} task${itemIds.length === 1 ? '' : 's'}? This cannot be undone.`,
        confirmLabel: 'Delete',
        variant: 'danger',
      }))
    ) {
      return false;
    }
    await bulk.mutateAsync({ itemIds, ...change });
    return true;
  }

  function openFromDue(task: TaskListItem) {
    setEditing({
      task,
      isTemplate: false,
      eventStartAt: task.event?.startAt ?? null,
      context: [task.event?.name, task.checklist.title, task.section.name]
        .filter(Boolean)
        .join(' · '),
    });
  }

  function openFromList(task: ChecklistItem, checklist: Checklist) {
    const section = checklist.sections.find((s) => s.id === task.sectionId);
    setEditing({
      task,
      isTemplate: checklist.isTemplate,
      eventStartAt: checklist.event?.startAt ?? eventStartAt,
      context: [checklist.title, section?.name].filter(Boolean).join(' · '),
    });
  }

  function save(updates: UpdateChecklistItemInput) {
    if (!editing) return;
    updateItem.mutate({ itemId: editing.task.id, updates }, { onSuccess: () => setEditing(null) });
  }

  const newList = () =>
    createChecklist.mutate(
      templates
        ? { isTemplate: true, title: 'New template', templateName: 'New template' }
        : { eventId, title: 'New task list' },
      { onSuccess: () => (templates ? undefined : update({ view: 'lists' })) },
    );

  const overdue = summary?.counts.overdue ?? 0;
  const dueToday = summary?.counts.today ?? 0;

  return (
    <div className="space-y-4">
      <div className="flex flex-wrap items-center justify-between gap-3">
        <div className="flex items-center gap-3 min-w-0">
          {leading}
          <div>
            <h1 className="text-2xl font-bold text-gray-900">
              {templates ? 'Checklist templates' : 'Tasks'}
            </h1>
            {templates && (
              <p className="text-sm text-gray-500">
                Dates here are days from the event — they become real dates when a template is used.
              </p>
            )}
            {!templates && summary && (
              <p className="text-sm text-gray-500" data-testid="task-summary">
                {overdue > 0 && <span className="text-red-700 font-medium">{overdue} overdue</span>}
                {overdue > 0 && dueToday > 0 && ' · '}
                {dueToday > 0 && (
                  <span className="text-amber-800 font-medium">{dueToday} due today</span>
                )}
                {overdue === 0 && dueToday === 0 && 'Nothing overdue or due today'}
              </p>
            )}
          </div>
        </div>
        <div className="flex items-center gap-2">
          {!templates && (
            <SegmentedControl
              options={VIEW_OPTIONS}
              value={view}
              onChange={(v) => update({ view: v })}
              ariaLabel="Group tasks"
            />
          )}
          <button
            type="button"
            onClick={newList}
            className="inline-flex items-center gap-1.5 px-3 py-2 bg-primary-600 text-white text-sm rounded-md hover:bg-primary-700"
          >
            <Plus size={16} />
            <span className="hidden sm:inline">{templates ? 'New template' : 'New list'}</span>
          </button>
        </div>
      </div>

      <TaskFilterBar
        value={filters}
        onChange={setFilters}
        assignees={assignees}
        // Template dates are offsets from an event, so there is nothing due.
        showDue={!templates}
        showStatus={!templates}
      />

      {view === 'due' ? (
        <TaskDueView
          filters={filters}
          todayKey={todayKey}
          eventId={eventId}
          assignees={assignees}
          onOpen={openFromDue}
          onToggle={onToggle}
          onDelete={onDelete}
          onBulk={onBulk}
          bulkBusy={bulk.isPending}
        />
      ) : listsLoading ? (
        <div className="space-y-3" aria-label="Loading task lists">
          {[0, 1].map((i) => (
            <div
              key={i}
              className="h-40 rounded-xl bg-white border border-gray-200 animate-pulse"
            />
          ))}
        </div>
      ) : checklists.length === 0 ? (
        <div className="text-center py-12 bg-white rounded-xl border border-gray-200">
          <CheckSquare className="mx-auto h-10 w-10 text-gray-400" />
          <p className="mt-3 font-medium text-gray-900">
            {templates ? 'No checklist templates yet' : 'No task lists yet'}
          </p>
          <p className="mt-1 text-sm text-gray-500">
            {templates
              ? 'Write a list once and put it on any event.'
              : 'Start a list and add tasks straight away — or copy one from Templates.'}
          </p>
          <button
            type="button"
            onClick={newList}
            className="mt-4 inline-flex items-center gap-1.5 px-3 py-2 bg-primary-600 text-white text-sm rounded-md hover:bg-primary-700"
          >
            <Plus size={16} /> New list
          </button>
        </div>
      ) : (
        <TaskListsView
          checklists={checklists}
          todayKey={todayKey}
          // Templates have no due dates or open/done state to filter by, and
          // no controls for them here — a due or status carried in the
          // address must not hide their tasks with no way to clear it.
          filters={templates ? { ...filters, due: [], status: 'open' } : filters}
          currentUserId={user?.id}
          eventStartAt={eventStartAt}
          showEvent={!eventId && !templates}
          openId={params.get('open')}
          onOpen={openFromList}
          onToggle={onToggle}
          onDelete={onDelete}
        />
      )}

      <TaskDetailModal
        task={editing?.task ?? null}
        isTemplate={editing?.isTemplate}
        eventStartAt={editing?.eventStartAt}
        context={editing?.context}
        todayKey={todayKey}
        assignees={assignees}
        assigneesLoading={usersLoading}
        onClose={() => setEditing(null)}
        onSave={save}
        onDelete={onDelete}
        saving={updateItem.isPending}
      />
    </div>
  );
}
